import type {
  AppSumoPromotion,
  LTDPromotion,
  Workspace,
  WorkspaceLicense,
} from "common/types";

type OutreachLicenseCountWorkspace = Pick<
  Workspace,
  "outreach_license_count" | "license_count" | "has_active_rental_subscription"
>;

export function hasActiveOutreachSubscription(
  workspace: Pick<Workspace, "role" | "subscription_status">,
): boolean {
  return (
    workspace.role === "owner" &&
    workspace.subscription_status &&
    workspace.subscription_status !== "cancelled"
  );
}

export function getOutreachLicenseCount(
  workspace: OutreachLicenseCountWorkspace | null | undefined,
  fallback?: number,
): number | undefined {
  if (workspace?.outreach_license_count != null) {
    return workspace.outreach_license_count;
  }
  // Legacy: license_count on list was total when rental exists — avoid wrong fallback
  if (
    !workspace?.has_active_rental_subscription &&
    workspace?.license_count != null
  ) {
    return workspace.license_count;
  }
  return fallback;
}

type WorkspaceDetailsForOutreach = Pick<
  Workspace,
  "outreach_license_count" | "subscription_status"
> & {
  subscription?: { status: Workspace["subscription_status"] } | null;
};

export function mergeWorkspacesWithDetails(
  workspaces: Workspace[] | undefined,
  detailsById: Map<string, WorkspaceDetailsForOutreach>,
): Workspace[] | undefined {
  if (!workspaces) return undefined;

  return workspaces.map((workspace) => {
    const details = detailsById.get(workspace.id);
    if (!details) return workspace;

    return {
      ...workspace,
      subscription_status:
        workspace.subscription_status ??
        details.subscription_status ??
        details.subscription?.status ??
        workspace.subscription_status,
      outreach_license_count:
        details.outreach_license_count ?? workspace.outreach_license_count,
    };
  });
}

export function getActiveOutreachWorkspaceSeatCounts(
  workspaces: Workspace[] | undefined,
): number[] {
  if (!workspaces) return [];

  return workspaces
    .filter(hasActiveOutreachSubscription)
    .map((workspace) => getOutreachLicenseCount(workspace) ?? 0);
}

export function getTotalActiveOutreachSeats(
  workspaces: Workspace[] | undefined,
): number {
  return getActiveOutreachWorkspaceSeatCounts(workspaces).reduce(
    (sum, count) => sum + count,
    0,
  );
}

// Pick "ltd" or "appsumo" seat pool for a workspace, preferring "ltd" if both can cover the target count.
// TODO: remove once "ltd" and "appsumo" promotions are merged into a single pool.
export function getLifetimeLicenseSource(
  ltdPromotion: LTDPromotion | undefined,
  appSumoPromotion: AppSumoPromotion | undefined,
  currentCount: number,
  targetCount: number,
  workspaceLicenses: WorkspaceLicense[] = [],
): "ltd" | "appsumo" | undefined {
  if (targetCount < currentCount) {
    // When releasing seats, pick the pool ("ltd" or "appsumo") with enough assigned seats to cover the decrease; if both qualify, prefer the one with more seats.
    const decreaseAmount = currentCount - targetCount;
    const currentLtdCount = workspaceLicenses.filter(
      ({ source }) => source === "ltd",
    ).length;
    const currentAppSumoCount = workspaceLicenses.filter(
      ({ source }) => source === "appsumo",
    ).length;

    if (currentLtdCount > 0 || currentAppSumoCount > 0) {
      if (currentLtdCount >= decreaseAmount) {
        return "ltd";
      }
      if (currentAppSumoCount >= decreaseAmount) {
        return "appsumo";
      }
      return currentLtdCount >= currentAppSumoCount ? "ltd" : "appsumo";
    }
  }

  // Adding seats (or no existing lifetime licenses to key off of): pick
  // whichever account-wide pool has enough room to cover the target count.
  const ltdMax = currentCount + (ltdPromotion?.unassigned_license_count ?? 0);
  const appSumoMax =
    currentCount + (appSumoPromotion?.unassigned_license_count ?? 0);

  if (ltdPromotion && targetCount <= ltdMax) {
    return "ltd";
  }
  if (appSumoPromotion && targetCount <= appSumoMax) {
    return "appsumo";
  }

  return ltdPromotion?.type ?? appSumoPromotion?.type;
}

// The API's `count` for POST /licenses/lifetime is the target count WITHIN
// the given `source` pool, not the workspace's overall lifetime_license_count.
// Convert the desired new workspace total into the equivalent source-scoped count.
export function getLifetimeLicenseSourceCount(
  workspaceLicenses: WorkspaceLicense[],
  source: "ltd" | "appsumo" | "agency",
  currentCount: number,
  targetCount: number,
): number {
  const currentSourceCount = workspaceLicenses.filter(
    (license) => license.source === source,
  ).length;
  return currentSourceCount + (targetCount - currentCount);
}
